/**
 * PackagePricingConfigurator — Interactive pricing configurator for PACKAGE products.
 *
 * Displays sets from packageStructure with appropriate selection controls
 * (checkboxes/radio buttons per selectionRule), a billing interval selector,
 * seats/quantity inputs for UNIT interpretation children, and a pricing table
 * with per-child rows and a "Razem" totals row.
 *
 * Features:
 * - Stale-while-revalidate: previous results stay visible during recalculation
 * - Debounce: 300ms for toggles, 600ms for number inputs
 * - Partial error handling: shows child errors inline, footnote for totals
 * - Frontend selectionRule validation
 *
 * @see AC-10 through AC-17, AC-20, AC-22 in docs/specifications/2026-03-22-billing-pricing-configurator.md
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { formatCentsAsCurrency } from "./formatCalculatorConfig";
import { SELECTION_RULE_LABELS } from "./constants";
import {
  EnrichedPackageStructure,
  EnrichedPackageStructureSet,
  EnrichedPackageStructureProduct,
  PackagePricingResponse,
  PackagePricingChild,
} from "../../../../types/adminBilling";
import { getPackagePricing } from "../../../../services/adminProductCatalogApi";

// =============================================================================
// Constants
// =============================================================================

const BILLING_INTERVAL_OPTIONS = [
  { value: "monthly", label: "Miesięczny" },
  { value: "quarterly", label: "Kwartalny" },
  { value: "yearly", label: "Roczny" },
];

const TOGGLE_DEBOUNCE_MS = 300;
const NUMBER_INPUT_DEBOUNCE_MS = 600;

// =============================================================================
// Types
// =============================================================================

export interface PackagePricingConfiguratorProps {
  productId: string;
  brandId: string;
  packageStructure: EnrichedPackageStructure | null;
}

interface PackagePricingConfiguratorInnerProps {
  productId: string;
  brandId: string;
  packageStructure: EnrichedPackageStructure;
}

interface FeatureOverridesState {
  [productId: string]: { [featureCode: string]: number };
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Extracts default selected product IDs from packageStructure (isDefault: true).
 */
function getDefaultSelectedIds(
  structure: EnrichedPackageStructure
): string[] {
  const ids: string[] = [];
  for (const set of structure.sets) {
    for (const product of set.products) {
      if (product.isDefault) {
        ids.push(product.productId);
      }
    }
  }
  return ids;
}

/**
 * Extracts initial feature overrides from packageStructure featureValues.
 * Only includes numeric values from featureValues.
 */
function getDefaultFeatureOverrides(
  structure: EnrichedPackageStructure
): FeatureOverridesState {
  const overrides: FeatureOverridesState = {};
  for (const set of structure.sets) {
    for (const product of set.products) {
      if (product.featureValues) {
        const numericEntries: Record<string, number> = {};
        for (const [key, value] of Object.entries(product.featureValues)) {
          if (typeof value === "number") {
            numericEntries[key] = value;
          }
        }
        if (Object.keys(numericEntries).length > 0) {
          overrides[product.productId] = numericEntries;
        }
      }
    }
  }
  return overrides;
}

/**
 * Checks whether unchecking a product in a set would violate AT_LEAST_ONE rule.
 */
function wouldViolateAtLeastOne(
  set: EnrichedPackageStructureSet,
  selectedIds: Set<string>,
  productIdToToggle: string
): boolean {
  if (set.selectionRule !== "AT_LEAST_ONE") return false;
  const currentlySelectedInSet = set.products.filter((p) =>
    selectedIds.has(p.productId)
  );
  // If only 1 selected and we're trying to uncheck it -> violation
  return (
    currentlySelectedInSet.length === 1 &&
    currentlySelectedInSet[0].productId === productIdToToggle
  );
}

/**
 * Formats the unit pricing display string.
 * "X PLN/stanowisko x N = Y PLN netto"
 */
function formatUnitPricing(child: PackagePricingChild): string | null {
  if (!child.quantity || child.quantity <= 0) return null;
  const unitPriceCents = Math.round(child.subtotalNetCents / child.quantity);
  const unitPrice = formatCentsAsCurrency(unitPriceCents);
  const total = formatCentsAsCurrency(child.subtotalNetCents);
  const label = child.unitLabel || "stanowisko";
  return `${unitPrice}/${label} x ${child.quantity} = ${total} netto`;
}

// =============================================================================
// Sub-components
// =============================================================================

/** Renders a single set with appropriate controls based on selectionRule */
const SetSection: React.FC<{
  set: EnrichedPackageStructureSet;
  setIndex: number;
  selectedIds: Set<string>;
  featureOverrides: FeatureOverridesState;
  onToggle: (productId: string, set: EnrichedPackageStructureSet) => void;
  onRadioChange: (
    productId: string | null,
    set: EnrichedPackageStructureSet
  ) => void;
  onFeatureChange: (
    productId: string,
    featureCode: string,
    value: number
  ) => void;
}> = ({ set, setIndex, selectedIds, featureOverrides, onToggle, onRadioChange, onFeatureChange }) => {
  const ruleLabel = SELECTION_RULE_LABELS[set.selectionRule] ?? set.selectionRule;
  const isRadio =
    set.selectionRule === "EXACTLY_ONE" || set.selectionRule === "AT_MOST_ONE";

  const selectionRuleTooltips: Record<string, string> = {
    ALL: "Wszystkie składniki są obowiązkowe",
    EXACTLY_ONE: "Należy wybrać dokładnie jeden składnik",
    AT_LEAST_ONE: "Należy wybrać co najmniej jeden składnik",
    AT_MOST_ONE: "Można wybrać co najwyżej jeden składnik",
    OPTIONAL: "Składniki są opcjonalne",
  };

  return (
    <fieldset data-testid="package-set" className="bg-neutral-50 rounded-md border border-neutral-200 p-4">
      <legend className="flex items-center justify-between mb-3 w-full">
        <span className="text-sm font-semibold text-neutral-900">{set.name}</span>
        <span
          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800"
          title={selectionRuleTooltips[set.selectionRule] ?? set.selectionRule}
        >
          {ruleLabel}
        </span>
      </legend>

      <div className="space-y-2">
        {isRadio ? (
          <RadioSetControls
            set={set}
            setIndex={setIndex}
            selectedIds={selectedIds}
            featureOverrides={featureOverrides}
            onRadioChange={onRadioChange}
            onFeatureChange={onFeatureChange}
          />
        ) : (
          <CheckboxSetControls
            set={set}
            selectedIds={selectedIds}
            featureOverrides={featureOverrides}
            onToggle={onToggle}
            onFeatureChange={onFeatureChange}
          />
        )}
      </div>
    </fieldset>
  );
};

/** Radio button controls for EXACTLY_ONE and AT_MOST_ONE rules */
const RadioSetControls: React.FC<{
  set: EnrichedPackageStructureSet;
  setIndex: number;
  selectedIds: Set<string>;
  featureOverrides: FeatureOverridesState;
  onRadioChange: (
    productId: string | null,
    set: EnrichedPackageStructureSet
  ) => void;
  onFeatureChange: (
    productId: string,
    featureCode: string,
    value: number
  ) => void;
}> = ({ set, setIndex, selectedIds, featureOverrides, onRadioChange, onFeatureChange }) => {
  const radioGroupName = `pkg-set-${setIndex}`;
  const selectedInSet = set.products.find((p) => selectedIds.has(p.productId));

  return (
    <>
      {set.selectionRule === "AT_MOST_ONE" && (
        <label className="flex items-center gap-2 p-2 rounded hover:bg-white cursor-pointer">
          <input
            type="radio"
            name={radioGroupName}
            checked={!selectedInSet}
            onChange={() => onRadioChange(null, set)}
          />
          <span className="text-sm text-neutral-600">Brak</span>
        </label>
      )}
      {set.products.map((product) => (
        <label
          key={product.productId}
          className="flex items-center gap-2 p-2 rounded hover:bg-white cursor-pointer"
        >
          <input
            type="radio"
            name={radioGroupName}
            checked={selectedIds.has(product.productId)}
            onChange={() => onRadioChange(product.productId, set)}
          />
          <span className="text-sm text-neutral-800">
            {product.productName ?? product.productId}
          </span>
          {product.isDefault && (
            <span className="text-xs text-green-700 bg-green-100 px-1.5 py-0.5 rounded">
              Domyślny
            </span>
          )}
          {selectedIds.has(product.productId) && (
            <FeatureInputs
              product={product}
              featureOverrides={featureOverrides}
              onFeatureChange={onFeatureChange}
            />
          )}
        </label>
      ))}
    </>
  );
};

/** Checkbox controls for ALL, AT_LEAST_ONE, OPTIONAL rules */
const CheckboxSetControls: React.FC<{
  set: EnrichedPackageStructureSet;
  selectedIds: Set<string>;
  featureOverrides: FeatureOverridesState;
  onToggle: (productId: string, set: EnrichedPackageStructureSet) => void;
  onFeatureChange: (
    productId: string,
    featureCode: string,
    value: number
  ) => void;
}> = ({ set, selectedIds, featureOverrides, onToggle, onFeatureChange }) => {
  const isAll = set.selectionRule === "ALL";

  return (
    <>
      {set.products.map((product) => {
        const isChecked = selectedIds.has(product.productId);
        const isLastInAtLeastOne =
          set.selectionRule === "AT_LEAST_ONE" &&
          wouldViolateAtLeastOne(set, selectedIds, product.productId) &&
          isChecked;
        const isDisabled = isAll || isLastInAtLeastOne;

        return (
          <label
            key={product.productId}
            className={`flex items-center gap-2 p-2 rounded ${
              isDisabled ? "opacity-70" : "hover:bg-white cursor-pointer"
            }`}
          >
            <input
              type="checkbox"
              checked={isChecked}
              disabled={isDisabled}
              onChange={() => {
                if (!isDisabled) {
                  onToggle(product.productId, set);
                }
              }}
              aria-label={product.productName ?? product.productId}
              title={isLastInAtLeastOne ? "Co najmniej jeden składnik musi być wybrany" : undefined}
            />
            <span className="text-sm text-neutral-800">
              {product.productName ?? product.productId}
            </span>
            {product.isDefault && (
              <span className="text-xs text-green-700 bg-green-100 px-1.5 py-0.5 rounded">
                Domyślny
              </span>
            )}
            {isChecked && (
              <FeatureInputs
                product={product}
                featureOverrides={featureOverrides}
                onFeatureChange={onFeatureChange}
              />
            )}
          </label>
        );
      })}
    </>
  );
};

/** Number inputs for feature values (e.g., seats) */
const FeatureInputs: React.FC<{
  product: EnrichedPackageStructureProduct;
  featureOverrides: FeatureOverridesState;
  onFeatureChange: (
    productId: string,
    featureCode: string,
    value: number
  ) => void;
}> = ({ product, featureOverrides, onFeatureChange }) => {
  const [invalidFields, setInvalidFields] = useState<Record<string, boolean>>({});

  if (!product.featureValues) return null;

  const numericFeatures = Object.entries(product.featureValues).filter(
    ([, value]) => typeof value === "number"
  );

  if (numericFeatures.length === 0) return null;

  return (
    <div className="flex items-center gap-2 ml-2">
      {numericFeatures.map(([featureCode]) => {
        const currentValue =
          featureOverrides[product.productId]?.[featureCode] ??
          (product.featureValues![featureCode] as number);
        const featureLabel = featureCode.split(":")[1]?.replace(/_/g, " ") ?? featureCode;
        const isInvalid = invalidFields[featureCode] ?? false;

        return (
          <div key={featureCode} className="flex flex-col">
            <div className="flex items-center gap-1">
              <input
                type="number"
                min="1"
                max="999"
                value={currentValue}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val >= 1 && val <= 999) {
                    setInvalidFields((prev) => ({ ...prev, [featureCode]: false }));
                    onFeatureChange(product.productId, featureCode, val);
                  } else {
                    setInvalidFields((prev) => ({ ...prev, [featureCode]: true }));
                  }
                }}
                aria-label={`Liczba ${featureLabel} dla ${product.productName ?? product.productId}`}
                className={`w-20 px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                  isInvalid ? "border-red-400" : "border-neutral-300"
                }`}
              />
              <span className="text-xs text-neutral-500">szt.</span>
            </div>
            {isInvalid && (
              <span className="text-xs text-red-500 mt-0.5">1 - 999</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

/** Pricing table with per-child rows and totals */
const PricingTable: React.FC<{
  response: PackagePricingResponse;
}> = ({ response }) => {
  const { children, totals } = response;
  const errorChildren = children.filter((c) => c.error);
  const errorCount = errorChildren.length;

  return (
    <div className="mt-4">
      <div className="bg-neutral-50 border border-neutral-200 rounded-md overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-neutral-100">
              <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                Składnik
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-neutral-500 uppercase">
                Netto
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-neutral-500 uppercase">
                VAT
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-neutral-500 uppercase">
                Brutto
              </th>
            </tr>
          </thead>
          <tbody>
            {children.map((child) => (
              <ChildRow key={child.productId} child={child} />
            ))}
            <tr className="border-t-2 border-neutral-300 bg-neutral-50 font-semibold">
              <td className="px-4 py-2 text-sm text-neutral-900">Razem</td>
              <td className="px-4 py-2 text-sm text-neutral-900 text-right font-mono">
                {formatCentsAsCurrency(totals.subtotalNetCents)}
              </td>
              <td className="px-4 py-2 text-sm text-neutral-600 text-right font-mono">
                {formatCentsAsCurrency(totals.vatCents)}
              </td>
              <td className="px-4 py-2 text-sm text-neutral-900 text-right font-mono">
                {formatCentsAsCurrency(totals.totalGrossCents)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {errorCount > 0 && (
        <p className="text-xs text-amber-600 mt-2 italic">
          * Suma nie uwzględnia {errorCount} składnik
          {errorCount === 1 ? "a" : "ów"} z błędami cennika
        </p>
      )}
    </div>
  );
};

/** Single child row in the pricing table */
const ChildRow: React.FC<{ child: PackagePricingChild }> = ({ child }) => {
  if (child.error) {
    return (
      <tr className="border-t border-neutral-100">
        <td className="px-4 py-2 text-sm text-neutral-800">
          {child.productName}
        </td>
        <td colSpan={3} className="px-4 py-2 text-sm text-red-600 text-right">
          {child.error}
        </td>
      </tr>
    );
  }

  const unitDisplay = child.quantity ? formatUnitPricing(child) : null;

  return (
    <tr className="border-t border-neutral-100">
      <td className="px-4 py-2 text-sm text-neutral-800">
        <div>{child.productName}</div>
        {unitDisplay && (
          <div className="text-xs text-neutral-500 mt-0.5">{unitDisplay}</div>
        )}
      </td>
      <td className="px-4 py-2 text-sm text-neutral-800 text-right font-mono">
        {formatCentsAsCurrency(child.subtotalNetCents)}
      </td>
      <td className="px-4 py-2 text-sm text-neutral-500 text-right font-mono">
        {formatCentsAsCurrency(child.vatCents)}
      </td>
      <td className="px-4 py-2 text-sm text-neutral-900 text-right font-mono font-medium">
        {formatCentsAsCurrency(child.totalGrossCents)}
      </td>
    </tr>
  );
};

// =============================================================================
// Main component
// =============================================================================

export const PackagePricingConfigurator: React.FC<
  PackagePricingConfiguratorProps
> = ({ productId, brandId, packageStructure }) => {
  // Guard: invalid packageStructure
  if (!packageStructure || !packageStructure.sets) {
    return (
      <div>
        <h4 className="text-sm font-semibold text-neutral-900 mb-2">Cennik</h4>
        <p className="text-sm text-red-600 italic">
          Nieprawidłowa struktura pakietu
        </p>
      </div>
    );
  }

  return (
    <PackagePricingConfiguratorInner
      productId={productId}
      brandId={brandId}
      packageStructure={packageStructure}
    />
  );
};

/**
 * Inner component that handles stateful logic. Separated from the guard
 * so hooks are always called in the same order.
 */
const PackagePricingConfiguratorInner: React.FC<
  PackagePricingConfiguratorInnerProps
> = ({ productId, brandId, packageStructure }) => {
  // ---- State ----
  const defaultSelected = useMemo(
    () => getDefaultSelectedIds(packageStructure),
    [packageStructure]
  );
  const defaultOverrides = useMemo(
    () => getDefaultFeatureOverrides(packageStructure),
    [packageStructure]
  );

  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(defaultSelected)
  );
  const [billingInterval, setBillingInterval] = useState("monthly");
  const [featureOverrides, setFeatureOverrides] =
    useState<FeatureOverridesState>(defaultOverrides);
  const [response, setResponse] = useState<PackagePricingResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRevalidating, setIsRevalidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ---- Debounce refs ----
  const toggleDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const numberDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const hasResponseRef = useRef(false);

  // ---- Fetch function ----
  const fetchPricing = useCallback(
    async (
      ids: string[],
      interval: string,
      overrides: FeatureOverridesState
    ) => {
      const currentRequestId = ++requestIdRef.current;

      if (hasResponseRef.current) {
        setIsRevalidating(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      try {
        // Build featureOverrides only for selected products that have overrides
        const filteredOverrides: Record<string, Record<string, number>> = {};
        for (const id of ids) {
          if (overrides[id] && Object.keys(overrides[id]).length > 0) {
            filteredOverrides[id] = overrides[id];
          }
        }

        const result = await getPackagePricing(productId, {
          brandId,
          billingInterval: interval,
          selectedProductIds: ids,
          featureOverrides:
            Object.keys(filteredOverrides).length > 0
              ? filteredOverrides
              : undefined,
        });

        // Only update if this is still the latest request
        if (currentRequestId === requestIdRef.current) {
          setResponse(result);
          hasResponseRef.current = true;
          setIsLoading(false);
          setIsRevalidating(false);
        }
      } catch (err) {
        if (currentRequestId === requestIdRef.current) {
          setError("Nie udało się pobrać cen. Spróbuj ponownie.");
          setIsLoading(false);
          setIsRevalidating(false);
        }
      }
    },
    [productId, brandId]
  );

  // ---- Trigger fetch with debounce ----
  const triggerFetch = useCallback(
    (debounceMs: number) => {
      // Clear existing debounce timers
      if (toggleDebounceRef.current) clearTimeout(toggleDebounceRef.current);
      if (numberDebounceRef.current) clearTimeout(numberDebounceRef.current);

      const timer = setTimeout(() => {
        const ids = Array.from(selectedIds);
        fetchPricing(ids, billingInterval, featureOverrides);
      }, debounceMs);

      if (debounceMs <= TOGGLE_DEBOUNCE_MS) {
        toggleDebounceRef.current = timer;
      } else {
        numberDebounceRef.current = timer;
      }
    },
    [selectedIds, billingInterval, featureOverrides, fetchPricing]
  );

  // ---- Initial load ----
  useEffect(() => {
    triggerFetch(TOGGLE_DEBOUNCE_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Handlers ----
  const handleToggle = useCallback(
    (toggleProductId: string, set: EnrichedPackageStructureSet) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(toggleProductId)) {
          // Check AT_LEAST_ONE constraint before removing
          if (wouldViolateAtLeastOne(set, next, toggleProductId)) {
            return prev; // Don't change
          }
          next.delete(toggleProductId);
        } else {
          next.add(toggleProductId);
        }
        return next;
      });
    },
    []
  );

  const handleRadioChange = useCallback(
    (newProductId: string | null, set: EnrichedPackageStructureSet) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        // Remove all products from this set
        for (const product of set.products) {
          next.delete(product.productId);
        }
        // Add the selected one (if not "brak")
        if (newProductId) {
          next.add(newProductId);
        }
        return next;
      });
    },
    []
  );

  const handleFeatureChange = useCallback(
    (changeProductId: string, featureCode: string, value: number) => {
      setFeatureOverrides((prev) => ({
        ...prev,
        [changeProductId]: {
          ...(prev[changeProductId] ?? {}),
          [featureCode]: value,
        },
      }));
    },
    []
  );

  const handleIntervalChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setBillingInterval(e.target.value);
    },
    []
  );

  const handleRetry = useCallback(() => {
    const ids = Array.from(selectedIds);
    fetchPricing(ids, billingInterval, featureOverrides);
  }, [selectedIds, billingInterval, featureOverrides, fetchPricing]);

  // ---- Trigger fetch on state changes ----
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    triggerFetch(TOGGLE_DEBOUNCE_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, billingInterval]);

  useEffect(() => {
    if (isFirstRender.current) return;
    triggerFetch(NUMBER_INPUT_DEBOUNCE_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureOverrides]);

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (toggleDebounceRef.current) clearTimeout(toggleDebounceRef.current);
      if (numberDebounceRef.current) clearTimeout(numberDebounceRef.current);
    };
  }, []);

  // ---- Computed: are there any default children? ----
  const hasDefaults = defaultSelected.length > 0;

  // ---- Render ----
  return (
    <div>
      <h4 className="text-sm font-semibold text-neutral-900 mb-2">Cennik</h4>

      {/* Billing interval selector (AC-13) */}
      <div className="mb-4">
        <label
          htmlFor="billing-interval-select"
          className="block text-xs font-medium text-neutral-600 mb-1"
        >
          Interwał rozliczeniowy
        </label>
        <select
          id="billing-interval-select"
          value={billingInterval}
          onChange={handleIntervalChange}
          className="w-48 px-3 py-1.5 text-sm border border-neutral-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
        >
          {BILLING_INTERVAL_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Set sections (AC-10) */}
      <div className="space-y-3 mb-4">
        {packageStructure.sets.map((set, index) => (
          <SetSection
            key={set.name}
            set={set}
            setIndex={index}
            selectedIds={selectedIds}
            featureOverrides={featureOverrides}
            onToggle={handleToggle}
            onRadioChange={handleRadioChange}
            onFeatureChange={handleFeatureChange}
          />
        ))}
      </div>

      {/* No defaults message */}
      {!hasDefaults && !response && !isLoading && !error && (
        <p className="text-sm text-neutral-500 italic">
          Brak domyślnych składników
        </p>
      )}

      {/* Error state with retry (edge case) */}
      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
          <button
            type="button"
            onClick={handleRetry}
            className="mt-2 px-3 py-1.5 text-sm font-medium text-red-700 border border-red-300 rounded-md hover:bg-red-100"
            aria-label="Spróbuj ponownie"
          >
            Spróbuj ponownie
          </button>
        </div>
      )}

      {/* Loading state (initial) */}
      {isLoading && !response && !error && (
        <div
          className="flex items-center gap-2 text-sm text-neutral-500 mt-4"
          role="status"
          aria-label="Ładowanie cennika"
        >
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
          <span>Ładowanie cennika...</span>
        </div>
      )}

      {/* Pricing table with stale-while-revalidate (AC-15, AC-17) */}
      {response && !error && (
        <div className="relative" aria-live="polite" aria-atomic="true">
          <h5 className="text-sm font-semibold text-neutral-900 mb-2 mt-2 border-t border-neutral-200 pt-3">
            Podsumowanie cenowe
          </h5>
          {isRevalidating && (
            <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center z-10 rounded">
              <span className="text-sm text-neutral-500 bg-white px-3 py-1 rounded-full border border-neutral-200 shadow-sm">
                Przeliczanie...
              </span>
            </div>
          )}
          <PricingTable response={response} />
          <p className="text-xs text-neutral-400 mt-2 italic">
            (i) Ceny orientacyjne. Rabaty indywidualne dla tenantów nie są
            uwzględnione.
          </p>
        </div>
      )}
    </div>
  );
};
